import { DIR_CATEGORIES } from '@src/components/sco/Advisors/data/dirCategories'
import { gql } from '@src/lib/gql'

/**
 * Fields a guest-safe advisor card renders. No `hourlyRate` / packages (L1). `isFeatured` drives the
 * public "Featured" label (L50); the review summary is the `SessionReviewSummaryFields` shape (L41),
 * inlined because this file uses the fraql `gql` tag.
 */
const CARD_FIELDS = `
  id
  advisorProfileId
  headline
  canAcceptSessions
  isFeatured
  categories {
    id
    key
    title
  }
  user {
    id
    userId
    slug
    fullName
    displayName
    title
    shortBio
    pictureUrl
    sessionReviewSummary {
      averageScore
      count
    }
  }
`

/** GraphQL alias for a category count field (`funding` → `c_funding`). */
export const landingCategoryAlias = (key: string) => `c_${key.replace(/[^a-zA-Z0-9]/g, '_')}`

/**
 * Landing browse block (L10), one round trip:
 * - `cards`: the top 8 in directory order — the default cards (with review summaries).
 * - `sample`: the top 60 in the same order, slim (no review summaries), only to pick skill-chip
 *   candidates from their skill tags. Kept separate so the landing doesn't run 60 summary reads.
 * - one `itemCount` per `DIR_CATEGORIES` key (same per-viewer visibility as the directory).
 * Directory `aggregates` are admin-only, so counts are per-category COUNT reads.
 */
export const ADVISORS_LANDING_BROWSE = gql`
  query AdvisorsLandingBrowse {
    cards: advisorDirectory(page: 1, pageSize: 8) {
      items {
        ${CARD_FIELDS}
      }
    }
    sample: advisorDirectory(page: 1, pageSize: 60) {
      items {
        id
        skillTagTitles
      }
      pagination {
        itemCount
      }
    }
    ${DIR_CATEGORIES.map(
      cat =>
        `${landingCategoryAlias(cat.key)}: advisorDirectory(categoryKey: "${cat.key}", page: 1, pageSize: 1) { pagination { itemCount } }`
    ).join('\n')}
  }
`

export const LANDING_SKILL_SLOTS = 6

/**
 * Directory keyword-search counts for up to six skill candidates — the same search "See all" runs
 * (`/advisors/directory?q=`), so a chip's count matches where it leads.
 */
export const ADVISORS_LANDING_SKILL_COUNTS = gql`
  query AdvisorsLandingSkillCounts($s0: String, $s1: String, $s2: String, $s3: String, $s4: String, $s5: String) {
    ${Array.from(
      { length: LANDING_SKILL_SLOTS },
      (_, i) => `s${i}: advisorDirectory(keywords: $s${i}, page: 1, pageSize: 1) { pagination { itemCount } }`
    ).join('\n')}
  }
`

/** Cards for a selected chip: a category filter or a keyword search, 8 at a time. */
export const ADVISORS_LANDING_FILTERED = gql`
  query AdvisorsLandingFiltered($categoryKey: String, $keywords: String) {
    advisorDirectory(categoryKey: $categoryKey, keywords: $keywords, page: 1, pageSize: 8) {
      items {
        ${CARD_FIELDS}
      }
      pagination {
        itemCount
      }
    }
  }
`
